import { useEffect, useRef, useMemo } from 'react'
import type {
  IndicatorOutput,
  IndicatorLabel,
} from '../../lib/indicators/types'
import { toLocalChartTime } from '../../lib/chartTime'
import { BandPrimitive } from '../../lib/chart/bandPrimitive'
import {
  IChartApi,
  ISeriesApi,
  LineSeries,
  LineStyle,
  LineData,
  LineWidth,
  SeriesType,
  Time,
} from 'lightweight-charts'
type LineSeries = ISeriesApi<'Line'>

interface IndicatorRendererProps {
  chart: IChartApi | null
  seriesRef: ISeriesApi<SeriesType> | null
  output: IndicatorOutput
  overlayTick: number
}

/** Map lightweight-charts LineStyle enum values to numeric values. */
const LINE_STYLE_MAP: Record<string, number> = {
  solid: LineStyle.Solid,
  dashed: LineStyle.Dashed,
  dotted: LineStyle.Dotted,
}

export function IndicatorRenderer({
  chart,
  seriesRef,
  output,
  overlayTick,
}: IndicatorRendererProps): React.ReactElement {
  const seriesMapRef = useRef<Map<string, LineSeries>>(new Map())
  const bandPrimitiveRef = useRef<BandPrimitive | null>(null)

  // Collect all series IDs from lines and price levels
  const ids = useMemo(() => {
    const set = new Set<string>()
    for (const line of output.lines) {
      set.add(line.id)
    }
    for (const level of output.priceLevels) {
      if (level.lineVisible === false) continue
      set.add(level.id)
    }
    return set
  }, [output])

  // Diff series: remove deleted, create new
  useEffect(() => {
    if (!chart) return

    const prevMap = seriesMapRef.current
    const newMap = new Map<string, LineSeries>(prevMap)

    // Remove series no longer in output
    for (const [id, series] of prevMap) {
      if (!ids.has(id)) {
        chart.removeSeries(series)
        newMap.delete(id)
      }
    }

    // Create new series for IDs that don't exist yet. v5's hit-test reads
    // an internal style getter that's only populated once visual options
    // exist — a series created with `{}` crashes on first hover. Seed defaults.
    for (const id of ids) {
      if (!newMap.has(id)) {
        newMap.set(
          id,
          chart.addSeries(LineSeries, {
            color: '#888888',
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
            // Indicator lines must not participate in the crosshair hit-test
            // — only the main candle series should. v5's SeriesLinePaneView
            // hit-test path is what crashes with "Value is null" on hover
            // when an indicator series is in a transient/empty state.
            crosshairMarkerVisible: false,
            autoscaleInfoProvider: () => null,
          }),
        )
      }
    }

    seriesMapRef.current = newMap
  }, [chart, ids])

  useEffect(() => {
    if (!chart) return
    let currentChart = chart
    return () => {
      if (!currentChart) return
      for (const series of seriesMapRef.current.values()) {
        try { currentChart.removeSeries(series) } catch {}
      }
      seriesMapRef.current.clear()
    }
  }, [chart])

  // Update line data for each indicator line
  useEffect(() => {
    if (!seriesRef) return
    const map = seriesMapRef.current
    for (const line of output.lines) {
      const series = map.get(line.id)
      if (!series) continue
      // Each indicator is isolated — a v5 internal throw on one line (e.g.
      // hit-test race during streaming updates) must not unmount the whole
      // chart. Swallow + log; next update tick will retry.
      try {
        const points = line.points.filter(
          (p): p is NonNullable<typeof p> => p != null,
        )
        series.applyOptions({
          color: line.color,
          lineWidth: line.width as LineWidth,
          lineStyle: LINE_STYLE_MAP[line.style ?? 'solid'],
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          autoscaleInfoProvider: () => null,
        })
        if (points.length === 0) {
          series.setData([])
          continue
        }
        // Dedupe + sort by *post-conversion* chart-time key. Two different source
        // ms values can collapse onto the same chart-second (DST fall-back, or
        // upstream duplicates), and lightweight-charts asserts strictly-unique
        // ascending times. Last-write-wins keeps the freshest value for each
        // bucket.
        const byTime = new Map<number, number>()
        for (const p of points) {
          byTime.set(toLocalChartTime(p.t) as unknown as number, p.value)
        }
        const data: LineData[] = Array.from(byTime.entries())
          .sort((a, b) => a[0] - b[0])
          .map(([time, value]) => ({ time: time as unknown as Time, value }))
        ;(series as ISeriesApi<'Line'>).setData(data)
      } catch (err) {
        console.warn('[IndicatorRenderer] line update failed', line.id, err)
      }
    }
  }, [output, seriesRef])

  // Update price level series
  useEffect(() => {
    if (!seriesRef) return

    const map = seriesMapRef.current
    for (const level of output.priceLevels) {
      if (level.lineVisible === false) continue
      const series = map.get(level.id)
      if (!series) continue

      try {
        const segments = level.segments.filter(
          (s): s is NonNullable<typeof s> => s != null,
        )

        const byTime = new Map<number, number>()
        for (const seg of segments) {
          const from = Number.isFinite(seg.fromT) ? seg.fromT : (output.lines[0]?.points[0]?.t ?? Date.now() - 86_400_000)
          const to = Number.isFinite(seg.toT) ? seg.toT : Date.now()
          byTime.set(toLocalChartTime(from) as unknown as number, seg.value)
          byTime.set(toLocalChartTime(to) as unknown as number, seg.value)
        }
        const data: LineData[] = Array.from(byTime.entries())
          .sort((a, b) => a[0] - b[0])
          .map(([time, value]) => ({ time: time as unknown as Time, value }))
        ;(series as ISeriesApi<'Line'>).setData(data)
        series.applyOptions({
          color: level.color,
          lineWidth: level.width as LineWidth,
          lineStyle: LINE_STYLE_MAP[level.style ?? 'solid'],
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          autoscaleInfoProvider: () => null,
        })
      } catch (err) {
        console.warn('[IndicatorRenderer] level update failed', level.id, err)
      }
    }
  }, [output, seriesRef])

  // Bands are painted onto the chart canvas via `BandPrimitive` (attached to
  // the main series). The chart drives the redraw on pan/zoom/resize, so
  // there's no per-tick React work for the band layer. Attach/detach is keyed
  // on the series ref so a chart-type swap (candle ↔ line) reattaches cleanly.
  useEffect(() => {
    if (!seriesRef) return
    const primitive = new BandPrimitive()
    bandPrimitiveRef.current = primitive
    seriesRef.attachPrimitive(primitive)
    return () => {
      try { seriesRef.detachPrimitive(primitive) } catch {}
      bandPrimitiveRef.current = null
    }
  }, [seriesRef])

  useEffect(() => {
    bandPrimitiveRef.current?.setBands(output.bands)
  }, [output.bands])

  // Labels still render as an SVG overlay sized to the chart container.
  // Recomputed when `overlayTick` bumps (resize / pan / zoom in the parent).
  const overlayElements = useMemo(() => {
    if (!chart || !seriesRef) return null
    if (output.labels.length === 0) return null
    try {
      const elements: React.ReactElement[] = []
      const timeScale = chart.timeScale()
      const renderLabels = (labels: IndicatorLabel[]) => {
        for (const label of labels) {
          const x = timeScale.timeToCoordinate(toLocalChartTime(label.t) as Time)
          const y = seriesRef.priceToCoordinate(label.value)
          if (x == null || y == null) continue
          const anchor = label.anchor ?? 'above'
          const align = label.align ?? 'center'
          const offsetY = anchor === 'above' ? -8 : 16
          let textAnchor: 'start' | 'end' | 'middle' = 'middle'
          let dx = 0
          switch (align) {
            case 'left':
              textAnchor = 'start'
              dx = 4
              break
            case 'right':
              textAnchor = 'end'
              dx = -4
              break
            case 'center':
            default:
              textAnchor = 'middle'
              break
          }
          elements.push(
            <text
              key={`label-${label.id}`}
              x={x + dx}
              y={y + offsetY}
              fill={label.color}
              fontSize={11}
              fontFamily="monospace"
              textAnchor={textAnchor}
              dominantBaseline="middle"
            >
              {label.text}
            </text>
          )
        }
      }
      renderLabels(output.labels)
      return elements.length > 0 ? (
        <svg
          className="absolute inset-0 pointer-events-none"
          width="100%"
          height="100%"
        >
          {elements}
        </svg>
      ) : null
    } catch {
      return null
    }
  }, [chart, seriesRef, output, overlayTick])

  return <>{overlayElements}</>
}
